import CamplaCoreService from "../services/CamplaCoreService";

class PluginHelper {

  static async readFileAsUTF8Text(file) {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);

    const encoding = PluginHelper.detectEncoding(bytes);
    const decoder = new TextDecoder(encoding, { fatal: false });

    return decoder.decode(bytes);
  }

  static detectEncoding(bytes) {
    // BOM detection
    if (bytes.length >= 3 &&
        bytes[0] === 0xEF &&
        bytes[1] === 0xBB &&
        bytes[2] === 0xBF) {
      return "utf-8";
    }

    if (bytes.length >= 2) {
      if (bytes[0] === 0xFF && bytes[1] === 0xFE) return "utf-16le";
      if (bytes[0] === 0xFE && bytes[1] === 0xFF) return "utf-16be";
    }

    // UTF-8 validation
    if (PluginHelper.isValidUTF8(bytes)) {
      return "utf-8";
    }

    // UTF-16 heuristic
    let zeroEven = 0;
    let zeroOdd = 0;
    const len = Math.min(bytes.length, 1000);

    for (let i = 0; i < len - 1; i += 2) {
      if (bytes[i] === 0) zeroEven++;
      if (bytes[i + 1] === 0) zeroOdd++;
    }

    if (zeroOdd > len / 10) return "utf-16le";
    if (zeroEven > len / 10) return "utf-16be";

    // fallback
    return "windows-1252";
  }

  static isValidUTF8(bytes) {
    let i = 0;

    while (i < bytes.length) {
      const b = bytes[i];

      if (b <= 0x7F) {
        i++;
        continue;
      }

      let count;

      if ((b & 0xE0) === 0xC0) count = 1;
      else if ((b & 0xF0) === 0xE0) count = 2;
      else if ((b & 0xF8) === 0xF0) count = 3;
      else return false;

      if (i + count >= bytes.length) return false;

      for (let j = 1; j <= count; j++) {
        if ((bytes[i + j] & 0xC0) !== 0x80) return false;
      }

      i += count + 1;
    }

    return true;
  }
  static async lookupUser(student){
    const userArr = await CamplaCoreService.lookupUser(student.email);
    if (userArr.length > 0){
      const user = Object.assign({}, userArr[0]);
      user["name"] = `${student.firstname} ${student.lastname}`;
      return {
        valid: true,
        email: student.email,
        user: user
      }
    }else{
      return {
        valid: false,
        email: student.email,
        user: null
      }
    }
  }

  static async validateStudentsEmails(students) {
    const promises = [];
    students.forEach(student => promises.push(PluginHelper.lookupUser(student)));
    const validatedStudents = await Promise.all(promises);
    const invalidStudents = validatedStudents.filter(is => !is.valid);
    if (invalidStudents.length > 0){
      throw new Error(`Invalid users found: ${invalidStudents.map(is => is.email).join(", ")}`);
    }
    return validatedStudents.map(vs => vs.user);
  }

  static async calculateAndCreateClasses(validatedData, userId, errorHandling){
    const maximumClassSize = 500;

    const createdClasses = [];
    const createdModuleClasses = [];

    if (validatedData.members.length <= maximumClassSize){
      const response = await PluginHelper.createClassAndModuleClass(validatedData, userId);
      if (response){
        createdClasses.push(response.createdClass);
        createdModuleClasses.push(response.createdModuleClass);
      }else{
        errorHandling();
        return null;
      }
    }else{
      const members = [...validatedData.members];
      const groupCount = Math.ceil(members.length / maximumClassSize);
      let elementsLeft = members.length;
      for (let i = 0; i < groupCount; i++){
        const end = elementsLeft > maximumClassSize ? (maximumClassSize * (i + 1)) : members.length;
        const subMembers = members.slice((maximumClassSize * i), end);

        const validatedDataClone = Object.assign({}, validatedData);
        validatedDataClone["members"] = subMembers;
        validatedDataClone["name"] = `${validatedDataClone["name"]}-${(i + 1)}`;


        const response = await PluginHelper.createClassAndModuleClass(validatedDataClone, userId);
        if (response){
          createdClasses.push(response.createdClass);
          createdModuleClasses.push(response.createdModuleClass);
        }else{
          errorHandling();
          return null;
        }

        elementsLeft = elementsLeft - maximumClassSize;
      }
    }
    return {createdClasses, createdModuleClasses}
  }

  static async createClassAndModuleClass(validatedData, userId){
    let createdClass = null;
    let createdModuleClass = null;
    try {
      createdClass = await CamplaCoreService.createClass({
        name: validatedData.name,
        members: validatedData.members,
        userId: userId
      });
      createdModuleClass = await CamplaCoreService.createModuleClass({
        userId: userId,
        classId: createdClass._id,
        moduleId: validatedData.moduleId,
        year: validatedData.year,
        isFall: validatedData.isFall
      });
      return {createdClass, createdModuleClass}
    }catch (err){
      if (createdClass) {
        try {
          await CamplaCoreService.deleteClass(createdClass._id);
        } catch (err) {}
      }
    }
    return null;
  }
}

export default PluginHelper;
